import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  chargerRapportPaie,
  derniersMois,
  libelleMois,
  libelleMoyenPaie,
  lignesCsvPaie,
  marquerPrimesPayees,
  moisPrecedent,
  MOYENS_PAIE,
  type LignePaie,
  type MoyenPaie,
  type RapportPaie,
} from '../../api/paie'
import { useDroits } from '../Contexte'
import { exporterCSV } from '../compta/briques'
import { dateCourte, fcfa } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'

/**
 * Paie livreurs (migration 0044) — les primes « forte demande » par livreur et par mois.
 *
 * Propriétaire et comptable (`paie_livreurs_mois`, `marquer_primes_payees`). Le montant d'une paie est calculé
 * par la base ; un mois payé n'est plus recalculé (instantané). Le paiement pose l'écriture D 6211 / C trésorerie
 * dans la comptabilité. Le salaire de base reste une dépense saisie à la main : cet écran ne le remplace pas.
 */
export function EcranPaie({
  orgId,
  surRafraichir,
  surNotifier,
}: {
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const droits = useDroits()
  const peutPayer = droits.peut('paie_livreurs') && !droits.apercu
  const [mois, setMois] = useState(() => moisPrecedent())
  const [rapport, setRapport] = useState<RapportPaie | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [chargement, setChargement] = useState(false)
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [aPayer, setAPayer] = useState<LignePaie | null>(null)
  const [moyen, setMoyen] = useState<MoyenPaie>('wave')
  const [reference, setReference] = useState('')
  const [enCours, setEnCours] = useState(false)

  const charger = useCallback(async () => {
    setChargement(true)
    setErreur(null)
    try {
      /* Aperçu de développement (`?apercu`, sans session) : un rapport fictif, absent du build de production. */
      if (import.meta.env.DEV && droits.apercu) {
        const { rapportPaieApercu } = await import('../apercu')
        setRapport(rapportPaieApercu(mois))
        return
      }
      setRapport(await chargerRapportPaie(orgId, mois))
    } catch (e) {
      setRapport(null)
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setChargement(false)
    }
  }, [orgId, mois, droits.apercu])

  useEffect(() => {
    void charger()
  }, [charger])

  async function payer() {
    if (!aPayer || enCours) return
    setEnCours(true)
    try {
      const code = await marquerPrimesPayees(orgId, { driverId: aPayer.driver_id, mois, moyen, reference, montantAffiche: aPayer.prime_fcfa })
      surNotifier(
        code === 'deja_paye'
          ? `${aPayer.nom} : ces primes étaient déjà marquées payées — rien n’a été payé deux fois`
          : `${aPayer.nom} : ${fcfa(aPayer.prime_fcfa)} de primes marqués payés (${libelleMoyenPaie(moyen)}) — écriture passée en comptabilité`,
      )
      setAPayer(null)
      setReference('')
      await Promise.all([charger(), surRafraichir()])
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  const options = derniersMois().map((m) => ({ valeur: m, libelle: libelleMois(m) }))

  return (
    <>
      <div className="bandeau" role="note">
        <strong>Primes de forte demande uniquement.</strong> Le salaire de base des livreurs reste saisi à la main, comme aujourd’hui
        (Comptabilité › Dépenses, « Rémunération des livreurs ») : cet écran ne le remplace pas. Chaque prime est la part livreur de la
        majoration figée sur la course ; un mois payé n’est plus recalculé.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', margin: '16px 0' }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Mois</label>
          <ListeDeroulante valeur={mois} pleineLargeur options={options} surChangement={(v) => { setOuvert(null); setMois(v) }} />
        </div>
        <button className="btn btn-outline btn-sm" style={{ width: 'auto' }} disabled={!rapport || rapport.livreurs.length === 0} onClick={() => rapport && exporterCSV(`paie-primes-livreurs-${mois.slice(0, 7)}.csv`, lignesCsvPaie(rapport), surNotifier)}>
          <Icone nom="download" /> Exporter (CSV)
        </button>
        {chargement && <span className="t-muted">Chargement…</span>}
      </div>

      {erreur && (
        <div className="bandeau erreur" role="alert">
          {erreur}
        </div>
      )}

      {rapport && (
        <>
          <div className="kpis">
            {[
              { libelle: `Primes — ${rapport.libelle_mois || libelleMois(mois)}`, valeur: fcfa(rapport.total_prime_fcfa), icone: 'spark' as const },
              { libelle: 'Déjà payé', valeur: fcfa(rapport.total_paye_fcfa), icone: 'check' as const },
              { libelle: 'Reste à payer', valeur: fcfa(rapport.total_a_payer_fcfa), icone: 'wallet' as const },
            ].map((k) => (
              <div className="kpi" data-anim key={k.libelle}>
                <div className="kpi-ic">
                  <Icone nom={k.icone} />
                </div>
                <div className="kpi-label">{k.libelle}</div>
                <div className="kpi-val">{k.valeur}</div>
              </div>
            ))}
          </div>

          {!rapport.termine && (
            <div className="bandeau alerte" role="status" style={{ marginTop: 12 }}>
              Ce mois n’est pas terminé : les chiffres bougent encore et le paiement n’est possible qu’à partir du 1er du mois suivant.
            </div>
          )}

          <div className="card" data-anim style={{ marginTop: 16 }}>
            <div className="card-head">
              <div>
                <h3>
                  <Icone nom="user" classe="h-ic" /> Livreurs
                </h3>
                <div className="ch-sub">Courses livrées dans le mois (heure de Bamako) · primes = part livreur des majorations</div>
              </div>
            </div>
            <div className="card-body flush">
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Livreur</th>
                      <th style={{ textAlign: 'right' }}>Courses livrées</th>
                      <th style={{ textAlign: 'right' }}>Courses majorées</th>
                      <th style={{ textAlign: 'right' }}>Majorations client</th>
                      <th style={{ textAlign: 'right' }}>Primes</th>
                      <th>Statut</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rapport.livreurs.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="t-muted">
                          Aucune course livrée ce mois-ci.
                        </td>
                      </tr>
                    ) : (
                      rapport.livreurs.map((l) => (
                        <Fragment key={l.driver_id}>
                          <tr>
                            <td className="fort">{l.nom}</td>
                            <td style={{ textAlign: 'right' }}>{l.courses_livrees}</td>
                            <td style={{ textAlign: 'right' }}>{l.courses_majorees}</td>
                            <td style={{ textAlign: 'right' }}>{fcfa(l.majoration_fcfa)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fcfa(l.prime_fcfa)}</td>
                            <td>
                              {l.paiement ? (
                                <span className="badge b-green" title={l.paiement.paye_par_nom ? `Par ${l.paiement.paye_par_nom}` : undefined}>
                                  Payé le {dateCourte(l.paiement.paye_le)} · {libelleMoyenPaie(l.paiement.moyen)}
                                  {l.paiement.reference ? ` · ${l.paiement.reference}` : ''}
                                </span>
                              ) : l.prime_fcfa > 0 ? (
                                <span className="badge b-gold">À payer</span>
                              ) : (
                                <span className="badge b-gray">Aucune prime</span>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                              {l.detail.length > 0 && (
                                <button className="btn btn-ghost btn-sm" style={{ width: 'auto' }} onClick={() => setOuvert(ouvert === l.driver_id ? null : l.driver_id)}>
                                  {ouvert === l.driver_id ? 'Masquer' : 'Détail'}
                                </button>
                              )}
                              {!l.paiement && l.prime_fcfa > 0 && peutPayer && (
                                <button
                                  className="btn btn-gold btn-sm"
                                  style={{ width: 'auto', marginLeft: 6 }}
                                  disabled={!rapport.termine}
                                  title={rapport.termine ? undefined : 'Possible une fois le mois terminé'}
                                  onClick={() => setAPayer(l)}
                                >
                                  <Icone nom="check" /> Marquer payé
                                </button>
                              )}
                            </td>
                          </tr>
                          {ouvert === l.driver_id && (
                            <tr>
                              <td colSpan={7} style={{ background: 'var(--soft, #f6f8f7)' }}>
                                <table className="table-saisie">
                                  <thead>
                                    <tr>
                                      <th>Livrée le</th>
                                      <th>Course</th>
                                      <th style={{ textAlign: 'right' }}>Majoration client</th>
                                      <th style={{ textAlign: 'right' }}>Part livreur</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {l.detail.map((c) => (
                                      <tr key={c.course_id}>
                                        <td>{dateCourte(c.livree_le)}</td>
                                        <td>{c.ref}</td>
                                        <td style={{ textAlign: 'right' }}>{fcfa(c.majoration_fcfa)}</td>
                                        <td style={{ textAlign: 'right' }}>{fcfa(c.prime_fcfa)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))
                    )}
                  </tbody>
                  {rapport.livreurs.length > 0 && (
                    <tfoot>
                      <tr>
                        <td className="fort">Total du mois</td>
                        <td style={{ textAlign: 'right' }}>{rapport.livreurs.reduce((s, l) => s + l.courses_livrees, 0)}</td>
                        <td style={{ textAlign: 'right' }}>{rapport.livreurs.reduce((s, l) => s + l.courses_majorees, 0)}</td>
                        <td style={{ textAlign: 'right' }}>{fcfa(rapport.livreurs.reduce((s, l) => s + l.majoration_fcfa, 0))}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800 }}>{fcfa(rapport.total_prime_fcfa)}</td>
                        <td colSpan={2} className="t-muted">
                          dont payé {fcfa(rapport.total_paye_fcfa)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {aPayer && (
        <Modale
          titre={`Primes de ${aPayer.nom} — ${rapport?.libelle_mois || libelleMois(mois)}`}
          ouverte
          surFermer={() => setAPayer(null)}
          surEnregistrer={() => void payer()}
          libelleEnregistrer="Marquer payé"
          enCours={enCours}
        >
          <div className="price-line total">
            <span>Montant à payer (calculé par la base)</span>
            <span className="pl-val">{fcfa(aPayer.prime_fcfa)}</span>
          </div>
          <div className="t-muted" style={{ fontSize: 12, margin: '4px 0 12px' }}>
            {aPayer.courses_majorees} course{aPayer.courses_majorees > 1 ? 's' : ''} majorée{aPayer.courses_majorees > 1 ? 's' : ''} sur {aPayer.courses_livrees} livrée
            {aPayer.courses_livrees > 1 ? 's' : ''}. Le paiement est journalisé (vous, maintenant, moyen, référence) et passe en comptabilité :
            débit 6211 « Rémunération des livreurs », crédit caisse, mobile money ou banque. Il ne s’annule pas.
          </div>
          <div className="field">
            <label>Moyen</label>
            <ListeDeroulante valeur={moyen} pleineLargeur options={MOYENS_PAIE.map((m) => ({ valeur: m.valeur, libelle: m.libelle }))} surChangement={(v) => setMoyen(v as MoyenPaie)} />
          </div>
          <div className="field">
            <label htmlFor="paie-ref">Référence (facultative)</label>
            <input id="paie-ref" maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ex. identifiant de transaction Wave" />
          </div>
        </Modale>
      )}
    </>
  )
}
